import Delaunator from 'delaunator'
import {
  bary,
  cart,
  cartBary,
  intersectLines,
  isPointInPolygon,
  isTriangleCollinear,
  TriangulatedPoint,
} from '@asset-viz/utils/fastMath'
import { convertCoordsToUVs } from '@asset-viz/utils/helpers'
import { createPolygonsForWrapping } from '@asset-viz/internalized/geometry'
import { useMemo } from 'react'
import {
  BufferAttribute,
  BufferGeometry,
  InterleavedBufferAttribute,
} from 'three'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentCoords,
  LineDirectionX,
  LineDirectionY,
} from 'types'

import { useSurfaceProps } from './SurfaceProvider'

const uvs = new Array<number>()

/**
 * Creates a new mesh using an existing surface and a set of polygon points in
 * UV coordinates along that surface, effectively using the polygon as a stencil
 * in 2D space which creates a corresponding 3D subset of the provided surface
 * @param polygonPoints The 2D polygon to use as a "stencil" on top of this surface
 */
export const useSurfaceStencil = (
  polygonPoints: ReadonlyArray<GeckoComponentCoords>,
  lineWrappingX: ReadonlyArray<LineDirectionX>,
  lineWrappingY: ReadonlyArray<LineDirectionY>
) => {
  const { bounds, geometry, wrapMode } = useSurfaceProps()
  const wrapsX = useMemo(
    () =>
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX ||
      wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX,
    [wrapMode]
  )
  const wrapsY = useMemo(
    () =>
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY ||
      wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY,
    [wrapMode]
  )
  return useMemo<BufferGeometry>(() => {
    // Perform some upfront error-checking
    let pointCount: number | undefined = undefined
    const attributes: {
      id: string
      attribute: BufferAttribute | InterleavedBufferAttribute
      array: number[]
    }[] = Object.entries(geometry.attributes).map(([id, attribute]) => {
      if (id === 'uv' && attribute.itemSize !== 2)
        throw new Error(`"uv" attribute item size not 2!`)

      const impliedPointCount = attribute.array.length / attribute.itemSize
      if (!Number.isInteger(impliedPointCount))
        throw new Error(`"${id}" array length not a factor of item size!`)

      if (pointCount === undefined) pointCount = impliedPointCount
      else if (impliedPointCount !== pointCount)
        throw new Error(`"${id}" array length not a factor of item size!`)

      return { id, attribute, array: new Array<number>() }
    })
    const inputUVs = geometry.getAttribute('uv')
    if (!pointCount || !inputUVs) throw new Error('No "uv" attribute!')
    uvs.length = 0

    for (let i = 0; i < polygonPoints.length; i++) {
      if (polygonPoints[i].componentID !== bounds.componentID) continue
      const converted = convertCoordsToUVs(polygonPoints[i], bounds)
      uvs.push(...converted)
    }
    // Experimentally-derived tolerance value due to accumulated floating
    // point arithmetic errors
    const tolerance = 1e-10

    const clippedPolygons = createPolygonsForWrapping(
      uvs,
      lineWrappingX,
      lineWrappingY,
      wrapsX,
      wrapsY
    )

    for (let t = 0; t < pointCount / 3; t++) {
      for (let pi = 0; pi < clippedPolygons.length; pi++) {
        const polygon = clippedPolygons[pi]
        // For each surface triangle, look at the UVs. Using these, we'll create
        // a new set of points (in surface triangle barycentric coordinates)
        // which fall inside the polygon described. Barycentric coordinates are
        // important here as they make it easier to interpolate non-UV measurement.
        const ax = inputUVs.array[t * 3 * inputUVs.itemSize]
        const ay = inputUVs.array[t * 3 * inputUVs.itemSize + 1]
        const aInPolygon = isPointInPolygon(ax, ay, polygon)

        const bx = inputUVs.array[(t * 3 + 1) * inputUVs.itemSize]
        const by = inputUVs.array[(t * 3 + 1) * inputUVs.itemSize + 1]
        const bInPolygon = isPointInPolygon(bx, by, polygon)

        const cx = inputUVs.array[(t * 3 + 2) * inputUVs.itemSize]
        const cy = inputUVs.array[(t * 3 + 2) * inputUVs.itemSize + 1]
        const cInPolygon = isPointInPolygon(cx, cy, polygon)

        const points = new Array<TriangulatedPoint>()

        let ex = polygon[polygon.length - 2] // End x & y
        let ey = polygon[polygon.length - 1]
        for (let i = 0; i < polygon.length; ) {
          const sx = polygon[i++] // Start x & y
          const sy = polygon[i++] // Start x & y

          // If the start of this polygon line is within the triangle
          // (its barycentric coordinates are >= 0) then include it
          const point = cart(sx, sy, ax, ay, bx, by, cx, cy)
          if (point.a >= 0 && point.b >= 0 && point.c >= 0) points.push(point)

          // abi, bci, & cai are guaranteed to be along the edges of the triangle
          const ab = intersectLines(ax, ay, bx, by, sx, sy, ex, ey)
          const bc = intersectLines(ax, ay, cx, cy, sx, sy, ex, ey)
          const ca = intersectLines(bx, by, cx, cy, sx, sy, ex, ey)

          const abIntersects = ab >= 0.0
          const bcIntersects = bc >= 0.0
          const caIntersects = ca >= 0.0

          if (abIntersects)
            points.push(bary(1 - ab, ab, 0, ax, ay, bx, by, cx, cy))
          if (bcIntersects)
            points.push(bary(1 - bc, 0, bc, ax, ay, bx, by, cx, cy))
          if (caIntersects)
            points.push(bary(0, 1 - ca, ca, ax, ay, bx, by, cx, cy))

          if ((!abIntersects || !bcIntersects) && bInPolygon)
            points.push(cartBary(bx, by, 0, 1, 0))
          if ((!bcIntersects || !caIntersects) && cInPolygon)
            points.push(cartBary(cx, cy, 0, 0, 1))
          if ((!caIntersects || !abIntersects) && aInPolygon)
            points.push(cartBary(ax, ay, 1, 0, 0))

          ex = sx
          ey = sy
        }

        // Convert from surface triangle barycentric coordinates to UVs, which
        // can then be Delaunay triangulated. Once Delaunay triangulation has
        // occurred, we have a valid mesh on top of the surface triangle which
        // represents the polygon provided.
        const delaunay = Delaunator.from(
          points,
          (p) => p.x,
          (p) => p.y
        )
        for (let d = 0; d < delaunay.triangles.length / 3; d++) {
          const p0 = points[delaunay.triangles[d * 3]]
          const p1 = points[delaunay.triangles[d * 3 + 1]]
          const p2 = points[delaunay.triangles[d * 3 + 2]]

          // Make sure that this triangle is not a degenerate case
          if (
            isTriangleCollinear(
              p0.x,
              p0.y,
              0,
              p1.x,
              p1.y,
              0,
              p2.x,
              p2.y,
              0,
              tolerance
            )
          )
            continue

          // Make sure that this triangle is not centered outside the polygon
          const cX = (p0.x + p1.x + p2.x) / 3.0 // Center point, ensures triangle is
          const cY = (p0.y + p1.y + p2.y) / 3.0 // not entirely outside polygon
          if (!isPointInPolygon(cX, cY, polygon))
            continue

            // For each point, for each attribute, interpolate according to
            // barycentric coordinates
            // delaunator returns triangles in clockwise rather than counter-clockwise direction
          ;[p2, p1, p0].forEach((p) => {
            attributes.forEach(({ attribute, array }) => {
              for (let k = 0; k < attribute.itemSize; k++)
                array.push(
                  p.a * attribute.array[t * 3 * attribute.itemSize + k] +
                    p.b *
                      attribute.array[(t * 3 + 1) * attribute.itemSize + k] +
                    p.c * attribute.array[(t * 3 + 2) * attribute.itemSize + k]
                )
            })
          })
        }
      }
    }

    const result = new BufferGeometry()
    attributes.forEach(({ id, attribute, array }) =>
      result.setAttribute(
        id,
        new BufferAttribute(new Float32Array(array), attribute.itemSize)
      )
    )
    return result
  }, [
    polygonPoints,
    lineWrappingX,
    lineWrappingY,
    bounds,
    geometry,
    wrapsX,
    wrapsY,
  ])
}
